import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { formatUserDisplayName } from '@/utils/formatters'
import { getCaretCoordinates } from '@/utils/caretPosition'
import { usePlanShares } from '@/hooks/usePlanShares'
import { useUserSearch } from '@/hooks/useUserSearch'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { UserSummary } from '@/api/shares'

// Matches an in-progress "@query" right at the caret: start-of-string or a
// preceding whitespace, then "@", then any run of non-whitespace/non-"@"
// characters (the search query so far).
const TRIGGER_REGEX = /(?:^|\s)@([^\s@]{0,50})$/

interface MentionSpan {
  start: number
  end: number
  userId: string
}

export interface MentionCandidate extends UserSummary {
  isCollaborator: boolean
}

interface DropdownState {
  atIndex: number
  // End of the text the picked mention replaces. Unset while typing (the
  // caret is the end); set when resolving an existing "@Name" token.
  replaceEnd?: number
  query: string
  activeIndex: number
  coords: { top: number; left: number; height: number }
}

/**
 * Text that looks like a mention ("@Name") but isn't one — e.g. pasted from
 * another comment, or typed with the dropdown dismissed. Rendered with a
 * dashed underline; clicking it opens the dropdown to resolve it.
 */
interface UnresolvedToken {
  start: number
  end: number
  query: string
  reason: 'notFound' | 'ambiguous' | 'untagged'
}

interface NameEntry {
  nameLower: string
  users: MentionCandidate[]
}

// Any "@" at the start of the text or after whitespace (so emails don't count).
const MENTION_START_REGEX = /(?<=^|\s)@/g
// Fallback guess at a name's extent when it doesn't match anyone: the first
// word plus up to three following capitalised words ("@Regular User").
const NAME_GUESS_REGEX = /^[^\s@]+(?: \p{Lu}[^\s@]*){0,3}/u
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)\]]+$/
const WORD_CHAR_REGEX = /[\p{L}\p{N}_]/u

interface TextDiff {
  start: number
  oldEnd: number
  newEnd: number
}

/** Prefix/suffix diff — a single controlled-textarea edit always changes one contiguous region. */
function diffRange(oldValue: string, newValue: string): TextDiff {
  const maxCommon = Math.min(oldValue.length, newValue.length)
  let prefix = 0
  while (prefix < maxCommon && oldValue[prefix] === newValue[prefix]) prefix++

  let suffix = 0
  const maxSuffix = maxCommon - prefix
  while (
    suffix < maxSuffix &&
    oldValue[oldValue.length - 1 - suffix] ===
      newValue[newValue.length - 1 - suffix]
  ) {
    suffix++
  }

  return {
    start: prefix,
    oldEnd: oldValue.length - suffix,
    newEnd: newValue.length - suffix,
  }
}

/**
 * Adjusts existing mention spans after a text edit. Spans entirely before or
 * after the changed region are kept (shifted if after); a span the edit
 * touched is no longer a valid mention and is dropped, falling back to plain
 * text.
 */
function reconcileSpans(
  spans: MentionSpan[],
  oldValue: string,
  newValue: string
): MentionSpan[] {
  if (spans.length === 0) return spans

  const { start, oldEnd } = diffRange(oldValue, newValue)
  const delta = newValue.length - oldValue.length

  const next: MentionSpan[] = []
  for (const span of spans) {
    if (span.end <= start) {
      next.push(span)
    } else if (span.start >= oldEnd) {
      next.push({ ...span, start: span.start + delta, end: span.end + delta })
    }
    // else: the edit overlapped this mention — no longer valid, drop it.
  }
  return next
}

/** Groups candidates by lower-cased display name, longest name first (so "Ann Smith" wins over "Ann"). */
function buildNameIndex(users: MentionCandidate[]): NameEntry[] {
  const byName = new Map<string, MentionCandidate[]>()
  for (const user of users) {
    const nameLower = formatUserDisplayName(user).toLowerCase()
    byName.set(nameLower, [...(byName.get(nameLower) ?? []), user])
  }
  return [...byName.entries()]
    .map(([nameLower, users]) => ({ nameLower, users }))
    .sort((a, b) => b.nameLower.length - a.nameLower.length)
}

/** Matches a known name right after the "@" at `atIndex`, ending on a word boundary. */
function matchNameAt(
  text: string,
  atIndex: number,
  nameIndex: NameEntry[]
): { end: number; users: MentionCandidate[] } | null {
  for (const entry of nameIndex) {
    const end = atIndex + 1 + entry.nameLower.length
    if (text.slice(atIndex + 1, end).toLowerCase() !== entry.nameLower) continue
    if (end < text.length && WORD_CHAR_REGEX.test(text[end])) continue
    return { end, users: entry.users }
  }
  return null
}

function isInsideSpan(index: number, spans: MentionSpan[]): boolean {
  return spans.some(s => index >= s.start && index < s.end)
}

/** Every "@..." in `text` that isn't already a mention span. */
function findUnresolvedTokens(
  text: string,
  spans: MentionSpan[],
  nameIndex: NameEntry[]
): UnresolvedToken[] {
  const tokens: UnresolvedToken[] = []
  for (const match of text.matchAll(MENTION_START_REGEX)) {
    const atIndex = match.index
    if (isInsideSpan(atIndex, spans)) continue

    const known = matchNameAt(text, atIndex, nameIndex)
    if (known) {
      tokens.push({
        start: atIndex,
        end: known.end,
        query: text.slice(atIndex + 1, known.end),
        reason: known.users.length > 1 ? 'ambiguous' : 'untagged',
      })
      continue
    }

    const guess = text
      .slice(atIndex + 1)
      .match(NAME_GUESS_REGEX)?.[0]
      .replace(TRAILING_PUNCTUATION_REGEX, '')
    if (!guess) continue
    tokens.push({
      start: atIndex,
      end: atIndex + 1 + guess.length,
      query: guess,
      reason: 'notFound',
    })
  }
  return tokens
}

/**
 * Turns "@Name" text inside [from, to) into mention spans when the name
 * matches exactly one candidate. Used for pasted text; ambiguous or unknown
 * names are left for the unresolved-token hint.
 */
function linkMentionsInRange(
  text: string,
  spans: MentionSpan[],
  from: number,
  to: number,
  nameIndex: NameEntry[]
): { spans: MentionSpan[]; linked: MentionCandidate[] } {
  const next = [...spans]
  const linked: MentionCandidate[] = []
  for (const match of text.matchAll(MENTION_START_REGEX)) {
    const atIndex = match.index
    if (atIndex < from || atIndex >= to) continue
    const known = matchNameAt(text, atIndex, nameIndex)
    if (!known || known.users.length !== 1) continue
    if (next.some(s => s.start < known.end && atIndex < s.end)) continue
    next.push({ start: atIndex, end: known.end, userId: known.users[0].id })
    linked.push(known.users[0])
  }
  return { spans: next, linked }
}

type MirrorSegment =
  | { kind: 'text'; value: string }
  | { kind: 'mention'; value: string }
  | { kind: 'unresolved'; value: string; tokenIndex: number }

/** Splits the visible text into plain, mention and unresolved runs for the highlight mirror. */
function buildMirrorSegments(
  text: string,
  spans: MentionSpan[],
  tokens: UnresolvedToken[]
): MirrorSegment[] {
  const marks = [
    ...spans.map(s => ({ start: s.start, end: s.end, tokenIndex: -1 })),
    ...tokens.map((t, i) => ({ start: t.start, end: t.end, tokenIndex: i })),
  ].sort((a, b) => a.start - b.start)

  const segments: MirrorSegment[] = []
  let cursor = 0
  for (const mark of marks) {
    if (mark.start < cursor) continue
    if (mark.start > cursor) {
      segments.push({ kind: 'text', value: text.slice(cursor, mark.start) })
    }
    const value = text.slice(mark.start, mark.end)
    segments.push(
      mark.tokenIndex === -1
        ? { kind: 'mention', value }
        : { kind: 'unresolved', value, tokenIndex: mark.tokenIndex }
    )
    cursor = mark.end
  }
  segments.push({ kind: 'text', value: text.slice(cursor) })
  return segments
}

function unresolvedTooltip(token: UnresolvedToken): string {
  switch (token.reason) {
    case 'ambiguous':
      return `Not tagged — more than one user is named "${token.query}". Click to pick one.`
    case 'untagged':
      return `Not tagged yet. Click to tag ${token.query}.`
    default:
      return `Not tagged — no user named "${token.query}". Click to search.`
  }
}

/** Replaces each mention span's visible "@Name" text with the wire format the backend expects, `@[uuid]`. */
function toRawContent(display: string, spans: MentionSpan[]): string {
  if (spans.length === 0) return display
  const sorted = [...spans].sort((a, b) => a.start - b.start)
  let result = ''
  let cursor = 0
  for (const span of sorted) {
    result += display.slice(cursor, span.start)
    result += `@[${span.userId}]`
    cursor = span.end
  }
  result += display.slice(cursor)
  return result
}

interface MentionCommentInputProps {
  planId: string
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  onMentionSelect?: (user: MentionCandidate) => void
  placeholder?: string
  disabled?: boolean
  rows?: number
  className?: string
  autoResize?: boolean
}

/**
 * A Textarea that supports Excel/Slack-style @mentions: typing "@" opens a
 * searchable people dropdown (plan collaborators first, org-wide search as a
 * fallback); picking someone shows a readable "@Jane Doe" in the box while
 * the value bubbled up via `onChange` carries the backend's raw `@[uuid]`
 * markup. See src/utils/mentions.ts for the wire format and CommentText for
 * the read-side rendering counterpart.
 *
 * A textarea can't style parts of its text, so a mirror div rendered behind a
 * transparent-text textarea draws the visible text: mentions in red, and
 * mention-looking text that didn't resolve to anyone with a dashed underline
 * (hover explains, click opens the dropdown to resolve it). Pasted "@Name"
 * text is linked automatically when it matches exactly one collaborator.
 */
export function MentionCommentInput({
  planId,
  value,
  onChange,
  onKeyDown,
  onMentionSelect,
  placeholder,
  disabled,
  rows,
  className,
  autoResize = false,
}: MentionCommentInputProps) {
  const [display, setDisplay] = useState(value)
  const [spans, setSpans] = useState<MentionSpan[]>([])
  const [dropdown, setDropdown] = useState<DropdownState | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mirrorRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const pendingCaretRef = useRef<number | null>(null)
  const [openUpward, setOpenUpward] = useState(false)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  const [hoveredToken, setHoveredToken] = useState<{
    index: number
    rect: { top: number; left: number; width: number; height: number }
  } | null>(null)

  const { userId: currentUserId } = useCurrentUser()
  const { data: sharesData, isLoading: sharesLoading } = usePlanShares(planId)
  const userSearch = useUserSearch({ query: dropdown?.query ?? '' })

  // The parent only ever externally resets `value` back to '' (after a
  // successful post) — otherwise this component is the source of truth for
  // `value`, bubbling the raw content up via onChange.
  useEffect(() => {
    if (value === '' && display !== '') {
      setDisplay('')
      setSpans([])
      setDropdown(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (!autoResize) return
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [display, autoResize])

  useLayoutEffect(() => {
    if (pendingCaretRef.current != null && textareaRef.current) {
      const pos = pendingCaretRef.current
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(pos, pos)
      pendingCaretRef.current = null
    }
    // `spans` too: resolving a token can leave the visible text unchanged.
  }, [display, spans])

  const collaborators = useMemo(() => {
    const list: UserSummary[] = []
    if (sharesData?.owner) list.push(sharesData.owner)
    for (const item of sharesData?.items ?? []) {
      if (item.user) list.push(item.user)
    }
    const seen = new Set<string>()
    return list.filter(u => {
      if (u.id === currentUserId || seen.has(u.id)) return false
      seen.add(u.id)
      return true
    })
  }, [sharesData, currentUserId])

  const nameIndex = useMemo(
    () =>
      buildNameIndex(collaborators.map(u => ({ ...u, isCollaborator: true }))),
    [collaborators]
  )

  // Skip the token the dropdown is currently open on — the user is still
  // typing/choosing it.
  const unresolvedTokens = useMemo(
    () =>
      findUnresolvedTokens(display, spans, nameIndex).filter(
        t => t.start !== dropdown?.atIndex
      ),
    [display, spans, nameIndex, dropdown?.atIndex]
  )

  const mirrorSegments = useMemo(
    () => buildMirrorSegments(display, spans, unresolvedTokens),
    [display, spans, unresolvedTokens]
  )

  // Keep the mirror's wrapping width and scroll in step with the textarea
  // (a vertical scrollbar narrows the textarea's content box).
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    const style = getComputedStyle(textarea)
    setScrollbarWidth(
      textarea.offsetWidth -
        textarea.clientWidth -
        parseFloat(style.borderLeftWidth) -
        parseFloat(style.borderRightWidth)
    )
    if (mirrorRef.current) mirrorRef.current.scrollTop = textarea.scrollTop
  }, [display])

  const suggestions = useMemo<MentionCandidate[]>(() => {
    if (!dropdown) return []
    const q = dropdown.query.trim().toLowerCase()
    const matchesQuery = (u: UserSummary) =>
      !q ||
      (u.display_name ?? '').toLowerCase().includes(q) ||
      (u.email ?? '').toLowerCase().includes(q)

    const localMatches: MentionCandidate[] = collaborators
      .filter(matchesQuery)
      .map(u => ({ ...u, isCollaborator: true }))

    const seen = new Set(localMatches.map(u => u.id))
    const searchResults =
      q.length >= 2 ? (userSearch.data?.pages.flatMap(p => p.users) ?? []) : []
    const externalMatches: MentionCandidate[] = searchResults
      .filter(u => u.id !== currentUserId && !seen.has(u.id))
      .map(u => ({ ...u, isCollaborator: false }))

    return [...localMatches, ...externalMatches].slice(0, 8)
  }, [dropdown, collaborators, userSearch.data, currentUserId])

  // True while we don't have a settled answer yet for the current query —
  // covers the initial shares load and the debounce window before the
  // org-wide search's query key/data catch up to what's been typed.
  const isLoadingSuggestions =
    !!dropdown &&
    (sharesLoading ||
      (dropdown.query.trim().length >= 2 &&
        (userSearch.isFetching || !userSearch.data)))

  function updateMentionTrigger(
    text: string,
    caretPos: number,
    currentSpans: MentionSpan[] = spans
  ) {
    const textarea = textareaRef.current
    // Caret placed inside an unresolved "@Name" (strictly inside, so typing
    // at its end keeps the normal behaviour): open the dropdown to resolve it.
    const token = findUnresolvedTokens(text, currentSpans, nameIndex).find(
      t => caretPos > t.start && caretPos < t.end
    )
    if (token && textarea) {
      setDropdown({
        atIndex: token.start,
        replaceEnd: token.end,
        query: token.query,
        activeIndex: 0,
        coords: getCaretCoordinates(textarea, token.start),
      })
      return
    }

    const before = text.slice(0, caretPos)
    const match = before.match(TRIGGER_REGEX)
    if (!match || !textarea) {
      setDropdown(null)
      return
    }
    const query = match[1]
    const atIndex = caretPos - 1 - query.length
    const coords = getCaretCoordinates(textarea, atIndex)
    setDropdown({ atIndex, query, activeIndex: 0, coords })
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDisplay = e.target.value
    let newSpans = reconcileSpans(spans, display, newDisplay)
    const diff = diffRange(display, newDisplay)
    // Multi-character insertions (paste, drop) may carry "@Name" text.
    if (diff.newEnd - diff.start > 1) {
      const result = linkMentionsInRange(
        newDisplay,
        newSpans,
        diff.start,
        diff.newEnd,
        nameIndex
      )
      newSpans = result.spans
      result.linked.forEach(user => onMentionSelect?.(user))
    }
    setDisplay(newDisplay)
    setSpans(newSpans)
    onChange(toRawContent(newDisplay, newSpans))
    updateMentionTrigger(
      newDisplay,
      e.target.selectionStart ?? newDisplay.length,
      newSpans
    )
  }

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement
    updateMentionTrigger(display, target.selectionStart ?? 0)
  }

  function selectSuggestion(user: MentionCandidate) {
    const textarea = textareaRef.current
    if (!dropdown || !textarea) return

    const replaceEnd =
      dropdown.replaceEnd ?? textarea.selectionStart ?? display.length
    const name = formatUserDisplayName(user)
    // Only add a separating space when the mention would otherwise run into
    // more text (or ends the comment); an existing space or punctuation like
    // "," / "." already separates it.
    const next = display[replaceEnd]
    const needsSpace = next === undefined || WORD_CHAR_REGEX.test(next)
    const insertText = needsSpace ? `@${name} ` : `@${name}`
    const newDisplay =
      display.slice(0, dropdown.atIndex) +
      insertText +
      display.slice(replaceEnd)
    const delta = insertText.length - (replaceEnd - dropdown.atIndex)

    const keptSpans = spans
      .filter(s => s.end <= dropdown.atIndex || s.start >= replaceEnd)
      .map(s =>
        s.start >= replaceEnd
          ? { ...s, start: s.start + delta, end: s.end + delta }
          : s
      )
    const newSpan: MentionSpan = {
      start: dropdown.atIndex,
      end: dropdown.atIndex + 1 + name.length,
      userId: user.id,
    }
    const newSpans = [...keptSpans, newSpan]

    setDisplay(newDisplay)
    setSpans(newSpans)
    setDropdown(null)
    onChange(toRawContent(newDisplay, newSpans))
    onMentionSelect?.(user)
    // Land after the separator: the one just inserted, or an existing space.
    pendingCaretRef.current =
      dropdown.atIndex + insertText.length + (next === ' ' ? 1 : 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (dropdown && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setDropdown(
          d =>
            d && { ...d, activeIndex: (d.activeIndex + 1) % suggestions.length }
        )
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setDropdown(
          d =>
            d && {
              ...d,
              activeIndex:
                (d.activeIndex - 1 + suggestions.length) % suggestions.length,
            }
        )
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const index = Math.min(dropdown.activeIndex, suggestions.length - 1)
        selectSuggestion(suggestions[index])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setDropdown(null)
        return
      }
    }
    onKeyDown?.(e)
  }

  // Decide whether the dropdown should open above the caret instead of below
  // it. Runs before paint so a flip never flickers: React commits the "below"
  // guess, this measures the actually-rendered box, and — only when the
  // decision changes — triggers one more pre-paint render with the flip.
  useLayoutEffect(() => {
    if (!dropdown || !dropdownRef.current || !textareaRef.current) {
      setOpenUpward(false)
      return
    }
    const margin = 8
    const rect = textareaRef.current.getBoundingClientRect()
    const caretTop = rect.top + dropdown.coords.top
    const caretBottom = caretTop + dropdown.coords.height
    const dropdownHeight = dropdownRef.current.offsetHeight
    const spaceBelow = window.innerHeight - caretBottom - margin
    const spaceAbove = caretTop - margin
    setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow)
  }, [dropdown, suggestions.length, isLoadingSuggestions])

  const dropdownPosition = useMemo(() => {
    if (!dropdown || !textareaRef.current) return null
    const rect = textareaRef.current.getBoundingClientRect()
    const caretTop = rect.top + dropdown.coords.top
    const caretBottom = caretTop + dropdown.coords.height
    return {
      top: openUpward ? undefined : caretBottom + 4,
      bottom: openUpward ? window.innerHeight - caretTop + 4 : undefined,
      left: rect.left + dropdown.coords.left,
    }
  }, [dropdown, openUpward])

  // The textarea sits on top of the mirror, so hover is hit-tested against
  // the mirror's unresolved-token boxes by pointer position.
  const handleMouseMove = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const mirror = mirrorRef.current
    const container = mirror?.parentElement
    if (!mirror || !container) return
    const containerRect = container.getBoundingClientRect()
    for (const el of mirror.querySelectorAll<HTMLElement>(
      '[data-token-index]'
    )) {
      for (const r of el.getClientRects()) {
        if (
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom
        ) {
          const index = Number(el.dataset.tokenIndex)
          if (hoveredToken?.index !== index) {
            setHoveredToken({
              index,
              rect: {
                top: r.top - containerRect.top,
                left: r.left - containerRect.left,
                width: r.width,
                height: r.height,
              },
            })
          }
          return
        }
      }
    }
    if (hoveredToken) setHoveredToken(null)
  }

  const hovered = hoveredToken && unresolvedTokens[hoveredToken.index]

  return (
    <div className="relative">
      <div
        ref={mirrorRef}
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 overflow-hidden whitespace-pre-wrap [overflow-wrap:break-word] rounded-md border border-transparent px-3 py-2 text-base text-foreground md:text-sm',
          disabled && 'opacity-50'
        )}
        style={{ right: scrollbarWidth }}
      >
        {mirrorSegments.map((segment, i) =>
          segment.kind === 'text' ? (
            <span key={i}>{segment.value}</span>
          ) : segment.kind === 'mention' ? (
            <span key={i} className="text-[#CF1F2E]">
              {segment.value}
            </span>
          ) : (
            <span
              key={i}
              data-token-index={segment.tokenIndex}
              className="underline decoration-muted-foreground decoration-dashed underline-offset-4"
            >
              {segment.value}
            </span>
          )
        )}
        {/* Keeps a trailing newline's empty last line in the layout. */}
        {'\u200b'}
      </div>
      <Textarea
        ref={textareaRef}
        value={display}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onBlur={() => setDropdown(null)}
        onScroll={e => {
          if (mirrorRef.current) {
            mirrorRef.current.scrollTop = e.currentTarget.scrollTop
          }
          setHoveredToken(null)
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredToken(null)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className={cn(
          'relative text-transparent caret-foreground',
          hovered && 'cursor-pointer',
          className
        )}
      />
      {hoveredToken && hovered && (
        <TooltipProvider>
          <Tooltip open>
            <TooltipTrigger asChild>
              <span
                className="pointer-events-none absolute"
                style={hoveredToken.rect}
              />
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={4}>
              {unresolvedTooltip(hovered)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {dropdown &&
        dropdownPosition &&
        (suggestions.length > 0 || isLoadingSuggestions) &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-50 w-72 overflow-hidden rounded-md border border-border bg-popover shadow-md"
            style={{
              top: dropdownPosition.top ?? 'auto',
              bottom: dropdownPosition.bottom ?? 'auto',
              left: dropdownPosition.left,
              // Radix's modal Dialog/Sheet sets `document.body.style.pointerEvents
              // = 'none'` while open, restoring it only on its own content node.
              // This dropdown is a separate `document.body` portal, so it needs
              // to explicitly opt back in or clicks on suggestions no-op inside
              // a Sheet (e.g. LineItemCommentsDrawer).
              pointerEvents: 'auto',
            }}
          >
            {suggestions.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching...
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto py-1">
                {suggestions.map((s, i) => {
                  const avatarColor = getAvatarColor(s.id)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-left text-sm',
                        i === dropdown.activeIndex
                          ? 'bg-accent'
                          : 'hover:bg-accent/50'
                      )}
                      onMouseDown={e => {
                        e.preventDefault()
                        selectSuggestion(s)
                      }}
                      onMouseEnter={() =>
                        setDropdown(d => d && { ...d, activeIndex: i })
                      }
                    >
                      <Avatar className="h-6 w-6 shrink-0">
                        <AvatarFallback
                          className={cn(
                            avatarColor.bg,
                            avatarColor.text,
                            'text-[10px] font-medium'
                          )}
                        >
                          {getInitials(s.display_name, s.email)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {formatUserDisplayName(s)}
                      </span>
                      {!s.isCollaborator && (
                        <span className="shrink-0 text-[10px] text-muted-foreground">
                          No plan access
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
